import { Controller } from "@hotwired/stimulus"

// FD-948: sidebar folders (each project's <cs-details>, plus the Workspace group)
// remember being opened or closed, and the current project's row toggles like a folder.
//
// Each folder has a default the server draws: open when it carries data-folder-open (the
// current project's folder, FD DEC-3, and the Wiki's Shared and Private sections), closed
// otherwise. The Workspace group is closed even on its own pages (FD DEC-3). A folder the
// user toggled keeps their choice instead, restored from localStorage on connect (every
// Drive visit renders a new sidebar) and again after a morph refresh, which would reset
// `open` to the server's markup.
//
// Defaults are never written. Only a toggle is, so the project you merely visit collapses
// again when you leave it, unless you opened it yourself.
//
// Recording listens to cs-details' own cs-show / cs-hide, which fire for every toggle:
// the chevron, a click anywhere non-interactive in the summary, and the keyboard (Enter,
// Space, arrows), none of which a click listener sees reliably (cs-details toggles inside
// its shadow root before the click bubbles out, and keys don't click at all). Restoring a
// state fires them too, which just writes the same value back.
//
// Links in a summary (a project's name) never toggle: cs-details ignores clicks on links
// and buttons. So the name is handled here:
//   - On the CURRENT project's row (data-folder-current) a plain click toggles the folder
//     in place instead of re-visiting Overview (still the first link in the tree).
//   - On any other row it navigates, and forgets whatever was remembered for that folder,
//     so the arriving page draws it open by default even if it was closed once. Going to a
//     project is choosing to see its links, but only while you're there.
//
// Markup: on the sidebar <nav>, data-action="click->sidebar-folders#click
// cs-show->sidebar-folders#remember cs-hide->sidebar-folders#remember". A folder is
// <cs-details data-folder="KEY"> (project id, or "workspace"). One delegated listener, not one
// per row: FD-497 keeps the ~90 repeated project rows as small as possible.
//
// FD-1217: a child project's folder is drawn inside its parent's. The server renders every
// ancestor of the current project open and marks it data-folder-ancestor; apply() leaves
// those alone, so a remembered "closed" can never hide the current project's row. A click on
// an ancestor's chevron still toggles it and is still remembered, for the pages where it is
// not an ancestor.
const STORAGE_KEY = "fd:sidebar-folders"
const FOLDER = "cs-details[data-folder]"

export default class extends Controller {
  connect() {
    this.apply = this.apply.bind(this)
    this.apply()
    document.addEventListener("turbo:morph", this.apply)
  }

  disconnect() {
    document.removeEventListener("turbo:morph", this.apply)
  }

  // A folder the viewer never toggled uses its default: closed, or open when it carries
  // data-folder-open (see the top of this file).
  apply() {
    const states = this.read()
    this.element.querySelectorAll(FOLDER).forEach((folder) => {
      if (folder.hasAttribute("data-folder-ancestor")) return // FD-1217: see above
      const key = folder.dataset.folder
      const open = key in states ? states[key] === 1 : folder.hasAttribute("data-folder-open")
      if (folder.open !== open) folder.open = open
    })
  }

  // cs-show / cs-hide from a folder (other cs-* elements in the sidebar, like the install
  // dialog, emit them too, hence the match).
  remember(event) {
    const folder = event.target
    if (!folder.matches?.(FOLDER) || !this.element.contains(folder)) return
    this.write(folder.dataset.folder, event.type === "cs-show")
  }

  click(event) {
    const link = event.target.closest("a")
    const folder = link?.closest(FOLDER)
    // Only a link in a folder's summary: the project's name, not one of its tree links.
    if (!folder || link.closest("[slot='summary']")?.parentElement !== folder) return
    // Leave cmd/ctrl/shift/middle clicks to the browser (open in a new tab and so on).
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return

    if (folder.hasAttribute("data-folder-current")) {
      // preventDefault stops Turbo (it skips a defaultPrevented click); the toggle then
      // records itself through cs-show / cs-hide.
      event.preventDefault()
      folder.open = !folder.open
    } else {
      this.forget(folder.dataset.folder)
    }
  }

  read() {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}")
      return parsed && typeof parsed === "object" ? parsed : {}
    } catch (_e) {
      return {}
    }
  }

  write(key, open) {
    this.store((states) => { states[key] = open ? 1 : 0 })
  }

  forget(key) {
    this.store((states) => { delete states[key] })
  }

  store(change) {
    try {
      const states = this.read()
      change(states)
      localStorage.setItem(STORAGE_KEY, JSON.stringify(states))
    } catch (_e) {
      // localStorage can throw (private mode, blocked site data, quota). Remembering a
      // folder is a nicety; the toggle itself has already happened.
    }
  }
}
